import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LayoutGrid, Plus, Search, Moon, Sun, LogOut, Command, FileText, CalendarClock, FolderKanban, Settings, FileSearch, Users, HelpCircle, Sparkles, Plug, Beaker } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import WelcomeGuide, { GUIDE_KEY } from "@/components/WelcomeGuide";
import HelpAvatar from "@/components/HelpAvatar";
import ProductTour, { TOUR_KEY, DEMO_TOUR_KEY } from "@/components/ProductTour";
import ContextualTip from "@/components/ContextualTip";

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const isAdmin = user?.role === "Admin" || (user?.email || "").toLowerCase() === "sent584@gmail.com";
  const [guideOpen, setGuideOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);
  const [demoTourRun, setDemoTourRun] = useState(false);
  const [demoMode, setDemoMode] = useState(localStorage.getItem("yl_demo_mode") === "1");
  const toggleDemo = (on) => {
    localStorage.removeItem(DEMO_TOUR_KEY);  // replay the demo tour each time demo mode is switched
    if (on) localStorage.setItem("yl_demo_mode", "1");
    else localStorage.removeItem("yl_demo_mode");
    setDemoMode(on);
    window.location.reload();
  };

  useEffect(() => {
    if (user && demoMode && !localStorage.getItem(DEMO_TOUR_KEY)) {
      const t = setTimeout(() => setDemoTourRun(true), 1300);
      return () => clearTimeout(t);
    }
  }, [user, demoMode]);

  useEffect(() => {
    if (user && !demoMode && !localStorage.getItem(TOUR_KEY)) {
      const t = setTimeout(() => setTourRun(true), 900);
      return () => clearTimeout(t);
    }
  }, [user, demoMode]);

  // Proactive personalized greeting on first login (after the tour is done)
  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem("yl_aria_greeted")) return;
    if (!localStorage.getItem(TOUR_KEY)) return;
    const t = setTimeout(() => { setHelpOpen(true); localStorage.setItem("yl_aria_greeted", "1"); }, 1000);
    return () => clearTimeout(t);
  }, [user, tourRun]);

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const runSearch = useCallback(async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); return; }
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(res.data.results);
    } catch { setResults([]); }
  }, []);

  const go = (r) => {
    setCmdOpen(false);
    if (r.type === "project") navigate(`/projects/${r.id}`);
    else if (r.type === "meeting") navigate(`/projects/${r.project_id}/meetings/${r.id}`);
    else if (r.type === "document") navigate(`/projects/${r.project_id}?tab=documents`);
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-16 lg:w-60 brand-gradient text-white flex flex-col z-30">
        <div className="h-16 flex items-center px-4 border-b border-white/10">
          <div className="hidden lg:block"><Logo size={30} /></div>
          <div className="lg:hidden mx-auto"><Logo compact size={30} /></div>
        </div>
        <nav className="flex-1 p-3 space-y-1" data-tour="sidebar-nav">
          {[
            { icon: LayoutGrid, label: "Workspace", path: "/" },
            { icon: FileSearch, label: "RFQ Studio", path: "/opportunities" },
            { icon: Plug, label: "SAP Connectors", path: "/connectors" },
            { icon: Plus, label: "New Project", path: "/projects/new" },
            ...(isAdmin ? [{ icon: Users, label: "Users", path: "/admin" }] : []),
            ...(isAdmin ? [{ icon: Settings, label: "Settings", path: "/settings" }] : []),
          ].map((it) => {
            const active = location.pathname === it.path;
            const tourId = it.label === "Workspace" ? "nav-workspace"
              : it.label === "RFQ Studio" ? "nav-rfq"
              : it.label === "SAP Connectors" ? "nav-connectors"
              : it.label === "New Project" ? "nav-new-project" : undefined;
            return (
              <button key={it.path} data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, '-')}`}
                data-tour={tourId}
                onClick={() => navigate(it.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
                <it.icon size={18} /><span className="hidden lg:inline">{it.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-3 text-[10px] text-white/40 hidden lg:block leading-relaxed">
          Yanthra Labs · Chennai<br />PRECISION · CARE · CONTINUITY
        </div>
      </aside>

      {/* Main */}
      <div className="pl-16 lg:pl-60">
        {/* Topbar */}
        <header className="h-16 glass border-b sticky top-0 z-20 flex items-center gap-3 px-4 lg:px-8">
          <button onClick={() => setCmdOpen(true)} data-testid="search-trigger" data-tour="search"
            className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/60 hover:bg-secondary rounded-lg px-3 py-2 w-full max-w-sm transition-colors">
            <Search size={15} /> <span className="flex-1 text-left">Search projects, meetings, docs…</span>
            <kbd className="hidden sm:flex items-center gap-0.5 text-[10px] bg-background border rounded px-1.5 py-0.5"><Command size={10} />K</kbd>
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2 mr-1" data-testid="demo-mode-toggle">
            <Beaker size={15} className={demoMode ? "text-[#c9a227]" : "text-muted-foreground"} />
            <span className="hidden sm:inline text-xs font-medium text-muted-foreground">Demo</span>
            <Switch checked={demoMode} onCheckedChange={toggleDemo} data-testid="demo-switch" aria-label="Demo mode" />
          </div>
          {demoMode && <span className="hidden md:inline text-[10px] font-semibold tracking-wide bg-[#c9a227]/15 text-[#a6821a] px-2 py-1 rounded-full" data-testid="demo-badge">DEMO DATA</span>}
          <Button variant="ghost" size="icon" onClick={() => setTourRun(true)} data-testid="help-trigger" data-tour="help-tour" aria-label="Guided tour">
            <HelpCircle size={18} />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2" data-testid="user-menu">
                <Avatar className="h-9 w-9 border-2 border-[#29abe2]/40">
                  <AvatarFallback className="bg-[#0f2557] text-white text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                <div className="text-[10px] mt-1 inline-block bg-[#29abe2]/15 text-[#1a7bb0] dark:text-[#5fc3f0] px-2 py-0.5 rounded-full">{user?.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-500">
                <LogOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 lg:p-8 max-w-[1500px] mx-auto">
          <Outlet />
        </main>
      </div>

      <CommandDialog open={cmdOpen} onOpenChange={setCmdOpen}>
        <CommandInput placeholder="Search projects, meetings, documents…" value={query} onValueChange={runSearch} data-testid="command-input" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {results.length > 0 && (
            <CommandGroup heading="Results">
              {results.map((r, i) => (
                <CommandItem key={i} onSelect={() => go(r)} value={r.label + i}>
                  {r.type === "project" ? <FolderKanban size={15} className="mr-2 text-[#29abe2]" /> :
                   r.type === "meeting" ? <CalendarClock size={15} className="mr-2 text-[#29abe2]" /> :
                   <FileText size={15} className="mr-2 text-[#29abe2]" />}
                  <span>{r.label}</span>
                  <span className="ml-auto text-xs text-muted-foreground capitalize">{r.type}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
      <WelcomeGuide open={guideOpen} onOpenChange={setGuideOpen} isAdmin={isAdmin} />

      {demoMode
        ? <ProductTour run={demoTourRun} demo onFinish={() => setDemoTourRun(false)} />
        : <ProductTour run={tourRun} onFinish={() => setTourRun(false)} />}

      {/* Floating Aria help assistant */}
      <button
        data-testid="help-avatar-trigger"
        data-tour="help-avatar"
        onClick={() => setHelpOpen((o) => !o)}
        aria-label="Open Aria, the AI help assistant"
        className="fixed bottom-5 right-4 lg:right-6 z-50 h-14 w-14 rounded-full brand-gradient text-white shadow-2xl grid place-items-center hover:scale-105 active:scale-95 transition-transform ring-4 ring-[#29abe2]/20">
        {helpOpen ? <HelpCircle size={22} /> : <Sparkles size={22} />}
        {!helpOpen && <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#29abe2] animate-pulse ring-2 ring-background" />}
      </button>
      <HelpAvatar open={helpOpen} onOpenChange={setHelpOpen} />
      <ContextualTip suppressed={helpOpen || tourRun} onOpenHelp={() => setHelpOpen(true)} />
    </div>
  );
}
